import React, { useEffect, useRef, useState } from "react";
import { Grid, Paper, Box, List, ListItem, ListItemAvatar, Avatar, ListItemText, ListItemSecondaryAction, Divider, Skeleton } from '@mui/material';
import Colors from "../../../../constants/Colors";
import { AccessTimeOutlined, Add, AddCircle, CheckCircleOutline, CloudOutlined, Edit, LocationOn, PersonOutline, ReceiptOutlined, Store, Web } from "@mui/icons-material";
import IconTextSection from "../../../common/IconTextSection";
import { withRouter } from "react-router";
import StyledSubHeader from "../../../common/StyledSubHeader";
import { ContainedButton, TooltipIconButton } from "../../../common/StyledButtons";
import { CARD_HEADER_STYLE, ICON_TITLE_TEXT_STYLE } from "../../../../constants/Styles";
import { getSiteById } from "../../../../selectors/SiteSelectors";
import StyledChip from "../../../common/StyledChip";
import { getIsUserAdmin, getUserId } from "../../../../selectors/UserSelectors";
import EditSiteAccessModal from "../../modals/EditSiteAccessModal";
import StyledPage from "../../../common/StyledPage";
import StyledSection from "../../../common/StyledSection";
import { getSiteSubscription } from "../../../../actions/internal/SiteActions";
import { showNotificationBar } from "../../../../actions/error-pages/NotificationBarActions";
import StyledBanner, { BANNER_VARIANTS } from "../../../common/StyledBanner";

const SiteAccountDetails = ({ history, match }) => {

  const subscription = useRef();

  const [isLoadingSubscription, setIsLoadingSubscription ] = useState(true);
  const [showEditAccessModal, setShowEditAccessModal] = useState(false);

  const siteId = match.params.site_id;
  const site = getSiteById(siteId);

  useEffect(() => {
    loadSession().then();
  }, [])

  const loadSession = async () => {
    try {
        const siteId = match.params.site_id
        const userId = getUserId()

        await getSiteSubscription(userId, siteId).then(resp => {
          subscription.current = resp;
        });
      } catch (err) {
        const error = await err;
        showNotificationBar('error', `Uh oh! A problem occurred while loading data. Details: ${error.message || 'No additional details.'}`);
      } finally {
        setIsLoadingSubscription(false);
      }
    }

  const prepareActions = () => {
    const tools = [
      {
        name: "Billing",
        icon: <ReceiptOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
        description: 'See invoices and billing preferences for your business.',
        link: `/dashboard/site/${siteId}/account/billing`
      }
    ]
    return tools;
  }

  const renderWebsite = () => {
    if (site && site.site_service_modules && site.site_service_modules.website && site.site_service_modules.website.domain )
    return <a style={{ color: Colors.primaryColor }} href={`https://${site.site_service_modules.website.domain.url}`} target='_blank'>{site.site_service_modules.website.domain.url}</a>
    else return <i>Not Available</i>
  }

  const renderQuickActionsSection = () => (
    <StyledSection
            title="Actions"
        >  
        <Grid container item xs={12} spacing={1}>
            {prepareActions().map((action) => (
          <Grid item xs={12} key={action.name}>
              <Paper
              variant="outlined"
              onClick={() =>  history.push(action.link)}
              sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '100%',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transform: 'scale(1.02)',
                    },
              }}
              >
              {/* Left: Icon + Text */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                  <Box>{action.icon}</Box>
                  <Box>
                  <div style={CARD_HEADER_STYLE}>{action.name}</div>
                  <p style={{ margin: 0 }}>{action.description}</p>
                  </Box>
              </Box>
              </Paper>
          </Grid>))}
        </Grid>
    </StyledSection>
)

const renderAccessContent = () => (
  <StyledSection
    title="User Access"
    rightAdornment={
      (site.site_security.is_primary_user || getIsUserAdmin()) && (
        <TooltipIconButton
          text="Manage User Access"
          icon={<Edit sx={{ fontSize: '18px' }} />}
          onClick={() => setShowEditAccessModal(true)}
        />
      )
    }
  >
    <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px', }}>
      <List style={{ flex: 1 }}>
        {site && site.site_security.accessible_users.map((user, index) => (
          <ListItem
            dense
            key={user.user_id || user.email}
            sx={{
              backgroundColor: '#fff',
              marginBottom: '5px',
              borderBottom: index !== site.site_security.accessible_users.length - 1 ? '1px solid #e0e0e0' : 'none',
            }}
          >
            <ListItemAvatar>
              <Avatar>
                <PersonOutline />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${user.first_name} ${user.last_name}`}
              secondary={user.email}
            />
            {user.is_primary_user && (
              <ListItemSecondaryAction>
                <StyledChip text="Primary" />
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
    </Paper>
  </StyledSection>
);

  const renderAddress = () => {
    const addr = site.site_details.site_address;
  
    if (!addr || !addr.address_line_1) {
      return <i>Not Provided</i>;
    }
  
    const line1 = addr.address_line_1 || '';
    const line2 = addr.address_line_2 ? ` ${addr.address_line_2}` : '';
    const cityStateZip = [addr.city, addr.state, addr.zipcode].filter(Boolean).join(' ');
  
    return (
      <span>
        {line1}{line2}
        {cityStateZip && `, ${cityStateZip}`}
      </span>
    );
  };

  const renderOverviewSection = () => (
    <StyledSection
    title="Business Details"
    >
    <Box
      sx={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: 2,
      }}
    >
      <IconTextSection
        icon={<Store sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Business Name"
        secondaryText={`${site.site_details.site_name}${site.site_details.site_structure ? `, ${site.site_details.site_structure}` : ''}`}
      />
      <Divider sx={{ my: 2 }} />
      <IconTextSection
        icon={<Web sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Primary Domain"
        secondaryText={renderWebsite()}
      />
      <Divider sx={{ my: 2 }} />
      <IconTextSection
        icon={<LocationOn sx={{ fontSize: '24px' }} />}
        primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE }}
        primaryText="Business Address"
        secondaryText={renderAddress()}
      />
    </Box>
      

    </StyledSection>
  )

  const hasActivePlan = () => {
    if (!subscription.current || !subscription.current.site_package || !subscription.current.site_package.plan) return false;
    return subscription.current.site_package.plan.is_active;
  }

  const isAciveLegacyPackage = () => {
    if (!subscription.current || !subscription.current.site_package) return false;
    return !subscription.current.site_package.plan 
    && subscription.current.site_package.package_details 
    && subscription.current.site_package.package_details.is_active;
  }

  const resolvePlanName = () => {
    if (!subscription.current || !subscription.current.site_package) return '---'
    return subscription.current.site_package.plan.name;
  }

  const resolveLegacyPackageName = () => {
    if (!subscription.current || !subscription.current.site_package) return '---'
    return subscription.current.site_package.package_details.name;
  }

  const resolvePlanDescription = () => {
    if (!subscription.current || !subscription.current.site_package) return '---'
    return subscription.current.site_package.plan.description;
  }

  const resolveLegacyPackageDescription = () => {
    if (!subscription.current || !subscription.current.site_package) return '---'
    return subscription.current.site_package.package_details.description;
  }

  const renderPerksSection = () => {
      if (!hasActivePlan()) return [];
      return subscription.current.site_package.plan.perks && subscription.current.site_package.plan.perks.map(perk => (
          <Box key={perk.name} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginRight: '18px', marginBottom: '10px' }}>
                  <CheckCircleOutline sx={{ color: Colors.primaryColor, fontSize: '20px' }} />
              </Box>
              <Box>
                  <h6 style={{ textAlign: 'left', padding: 0, fontSize: '16px', fontWeight: 'normal' }}>{perk.name}</h6>
              </Box>
          </Box>
      ))
  }

  const renderPlanDetails = () => {
      if (!hasActivePlan() || isLoadingSubscription) {
          return (
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                  <Box>
                      <h6 style={{ textAlign: 'left', padding: 0, fontSize: '20px' }}>{isLoadingSubscription ? <Skeleton variant="rectangular" width={175} height={30} sx={{ margin: '3px'}} /> : 'No Active BCK Plan'}</h6>
                      {isLoadingSubscription ? <Skeleton variant="rectangular" width={300} height={70} sx={{ margin: '3px'}} /> : <p style={{ marginTop: '8px', textAlign: 'left' }}>No BCK plan is currently associated with this account. Get started by finding the plan that best fits the business, so BCK can help save time, reduce stress, and keep things running smoothly online.</p>}
                      {!isLoadingSubscription && <ContainedButton onClick={() => window.open('https://consultwithkyle.com/plans', '_blank')}>
                        Explore Plans
                      </ContainedButton> }
                  </Box>
              </Box>
          )
      }
      return (
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
              <Box>
                  <h6 style={{ textAlign: 'left', padding: 0, fontSize: '20px' }}>{resolvePlanName()}</h6>
                  <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '700px' }}>{resolvePlanDescription()}</p>
              </Box>
          </Box>
      )
  }

  const renderLegacyPackageDetails = () => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
            <Box>
                <h6 style={{ textAlign: 'left', padding: 0 }}>{resolveLegacyPackageName()}</h6>
                <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '700px' }}>{resolveLegacyPackageDescription()}</p>
            </Box>
        </Box>
    )
  }

  const renderAddOnsSection = () => {
    const extraProducts =
      subscription.current &&
      subscription.current.site_package &&
      subscription.current.site_package.extra_products
        ? subscription.current.site_package.extra_products
        : [];
  
    if (
      isLoadingSubscription ||
      (!hasActivePlan() && !isAciveLegacyPackage()) ||
      extraProducts.length === 0
    ) {
      return;
    }
  
    return (
      <>
        <Divider sx={{ marginBottom: '20px', marginTop: '10px' }} />
  
        <h6
          style={{
            textAlign: 'left',
            padding: 0,
            fontSize: '18px',
            marginBottom: '20px',
          }}
        >
          Add-Ons
        </h6>
  
        <Box
          sx={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: 2,
          }}
        >
          {extraProducts.map((prod, index) => (
            <React.Fragment key={prod.id || prod.name || index}>
              <IconTextSection
                icon={<AddCircle sx={{ color: '#333', fontSize: '24px' }} />}
                primaryTextStyle={{ ...ICON_TITLE_TEXT_STYLE, fontWeight: 600 }}
                primaryText={prod.name || 'Untitled Add-On'}
                secondaryText={prod.description || 'Details coming soon.'}
              />
  
              {index !== extraProducts.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </Box>
      </>
    );
  };

  const renderPlanSection = () => {
    const isLegacy = isAciveLegacyPackage();
    return (<StyledSection
            title="Plan Details"
            rightAdornment={<StyledChip
                text={(hasActivePlan() || isLegacy) ? 'Active' : 'Inactive'}
                sx={{ backgroundColor: (hasActivePlan() || isLegacy)? Colors.primaryColor : Colors.greyColor }}
                />}
        >
        {isLegacy ? renderLegacyPackageDetails() : renderPlanDetails()}
        {renderPerksSection()}
        {renderAddOnsSection()}
    </StyledSection>)
  }

  const renderPlanChangeNotification = () => {
      if (!subscription.current || (subscription.current.site_package && subscription.current.site_package.package_age < 90)) return;
      const hasActivePlan = !!subscription.current.site_package.plan;

      
      if (hasActivePlan) {
        return (
        <Box sx={{ marginTop: '10px' }}>
            <StyledBanner
                title={`Your new ${subscription.current.site_package.plan.name}`}
                description={`Welcome to your new BCK plan, an all-inclusive bundle of tools, support, and services designed to help business run smoothly online.`}
                variant={BANNER_VARIANTS.IMPORTANT}
            />    
        </Box>
        )
      } else {
          return (
              <Box sx={{ marginTop: '10px' }}>
                  <StyledBanner
                      title={"Your BCK Services Are Being Combined"}
                      description={`BCK services will soon be combined into one all-inclusive plan to make everything easier to manage. More information will be shared shortly, and no action is needed. Please reach out with any questions.`}
                      variant={BANNER_VARIANTS.IMPORTANT}
                  />    
              </Box>
              )
      }
  }

  /*
    TO ADD TO PAGE:
    - SOCIAL MEDIA URLS
    - BUSINESS DESCRIPTION/ABOUT MY BUSINESS (NEED FOR SEO SIGN UP)
    - BUSINESS CATEGORIES
    - GOOGLE BUSINESS PROFILE LINK?
  */

  return (
    <>
     <StyledPage>
        <Grid container >
            <Grid item xs={12}>
              <StyledSubHeader title="Account" />
            </Grid>
            <Grid item xs={12}>
              {renderPlanChangeNotification()}
            </Grid>
            <Grid item xs={12}>
              {renderPlanSection()}
            </Grid>
            <Grid item xs={12}>
              {renderOverviewSection()}
            </Grid>
            <Grid item xs={12}>
            {renderAccessContent()}
            </Grid>
            <Grid item xs={12}>
              {renderQuickActionsSection()}
            </Grid>
      </Grid>
    </StyledPage>
    <EditSiteAccessModal
        open={showEditAccessModal}
        onClose={() => setShowEditAccessModal(false)}
        site={site}
      />
    </>
  )
}

export default withRouter(SiteAccountDetails);
